import React from "react";
import { Container, Box, Typography, Paper, Stack, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ---------- Constants ---------- */
const COLORS = {
  primary: "#0ea5e9",
  success: "#22c55e",
  info: "#2563eb",
  accentPurple: "#7c3aed",
  grayDark: "#334155",
  grayLight: "#64748b",
};

const FONTS = {
  base: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const SECTION_STYLE = (accent, bg) => ({
  p: 5,
  mb: 6,
  borderRadius: 3,
  background: bg,
  borderLeft: `6px solid ${accent}`,
});

/* ---------- Reusable Components ---------- */

const DocImage = ({ src, label }) => (
  <Box mt={3}>
    <Paper
      elevation={0}
      sx={{
        p: 2,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 2,
      }}
    >
      <img src={src} alt={label} style={{ width: "100%" }} />
    </Paper>
    <Typography
      variant="caption"
      sx={{ mt: 1, display: "block", color: "#6b7280" }}
    >
      {label}
    </Typography>
  </Box>
);

const Milestone = ({ text }) => (
  <Alert
    icon={<CheckCircleIcon />}
    severity="success"
    sx={{
      mt: 4,
      borderRadius: 2,
      background: "#ecfeff",
      color: "#065f46",
      fontWeight: 500,
    }}
  >
    {text}
  </Alert>
);

/* ---------- Guide Page ---------- */

export default function GuidePage() {
  return (
    <Container
      maxWidth="md"
      sx={{
        py: 8,
        fontFamily: FONTS.base,
      }}
    >
      {/* COVER */}
      <Box mb={8}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ letterSpacing: "-0.02em", mb: 2 }}
        >
          Guide to using the Atttic3D App with Shopify Stores
        </Typography>
        <Typography
          sx={{
            fontSize: 17,
            lineHeight: 1.7,
            color: "#374151",
            maxWidth: 720,
          }}
        >
          This guide explains how to install the Atttic3D Shopify App, create
          and manage Atttic Projects, link 3D Experiences to Shopify product
          variants, and publish them on Product Detail Pages (PDPs).
        </Typography>
      </Box>

      {/* INTRO */}
      <Paper sx={SECTION_STYLE(COLORS.grayLight, "#f8fafc")}>
        <Typography paragraph>
          Our Shopify App makes it easy to publish Atttic Projects on your
          Shopify stores. The Atttic Console empowers you to manage which 3D
          Experiences are rendered for each of your products. Let’s walk through
          the steps.
        </Typography>
        <Typography>
          If you feel stuck at any point, please contact us at{" "}
          <a
            href="https://www.atttic.com/contactus"
            target="_blank"
            rel="noreferrer"
          >
            https://www.atttic.com/contactus
          </a>
          . We should be able to revert shortly and assist you.
        </Typography>
      </Paper>

      {/* SECTION 1: Getting Started */}
      <Paper sx={SECTION_STYLE(COLORS.primary, "#f0f9ff")}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          1. Getting started with the Atttic3D App for Shopify
        </Typography>

        <Stack spacing={2}>
          <Typography>
            <strong>a)</strong> Install the Atttic3D app on your store using{" "}
            <a
              href="https://connect.atttic.com/auth?shop=atttic-test-store-for-review.myshopify.com"
              target="_blank"
              rel="noreferrer"
            >
              this link
            </a>
            . Admin privileges may be required.
          </Typography>

          <Typography>
            <strong>b)</strong> After installation, you will be redirected to{" "}
            <a
              href="https://console.atttic.com"
              target="_blank"
              rel="noreferrer"
            >
              Atttic Console
            </a>
            . Create an account if new or log in.
          </Typography>

          <Typography>
            <strong>c)</strong> Click “Add new project” to create a new Atttic
            Project.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig01_AttticConsole_AddingNewProject.png"
            label="Fig 01 — Creating a new Atttic Project"
          />

          <Typography>
            <strong>d)</strong> Click “Add Shop” to link your Shopify store.
          </Typography>
          <Typography>
            <strong>e)</strong> If linking fails, verify the shop is not linked
            elsewhere. Reinstall app if needed.
          </Typography>
          <Typography>
            <strong>f)</strong> Once linked, the button changes to “View Shop”,
            listing all product variants.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig02_AttticConsole_AddingShoptoProject.png"
            label="Fig 02 — Shopify store successfully linked"
          />
        </Stack>

        <Milestone text="You are now ready to choose which Atttic Experiences are published to your PDPs." />
      </Paper>

      {/* SECTION 2: Building 3D Experiences */}
      <Paper sx={SECTION_STYLE(COLORS.success, "#f0fdf4")}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          2. Building 3D Experiences with Atttic
        </Typography>
        <Typography paragraph>
          Atttic customizes each 3D Experience to your use-case. Please connect
          with the Atttic team to build Experiences unique to your brand.
        </Typography>
        <Typography>
          Once created, these Experiences become available in the Atttic Console
          for publishing on Shopify and other platforms.
        </Typography>
      </Paper>

      {/* SECTION 3: Linking 3D Showcase */}
      <Paper sx={SECTION_STYLE(COLORS.info, "#eef2ff")}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          3. Linking Products with 3D Showcase Experiences
        </Typography>
        <Typography paragraph>
          The 3D Showcase is a PDP-native Experience that enables customers to
          visually explore products in an interactive 3D environment.
        </Typography>

        <Stack spacing={2}>
          <Typography>
            <strong>i)</strong> Select the variant.
          </Typography>
          <Typography>
            <strong>ii)</strong> Click the Showcase linkage icon.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig04_AttticConsole_ShowcasetoVariant_LinkageIcon.png"
            label="Fig 04 — Showcase linkage icon"
          />

          <Typography>
            <strong>iii)</strong> Select the correct product.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig05_AttticConsole_ShowcasetoVariant_AttticProductSelection.png"
            label="Fig 05 — Product selection"
          />

          <Typography>
            <strong>iv)</strong> Select the appropriate variant and Showcase,
            then save.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig06_AttticConsole_ShowcasetoVariant_AttticVariant+ShowcaseSelection.png"
            label="Fig 06 — Variant and Showcase selection"
          />

          <Typography>
            <strong>v)</strong> If no Showcase appears, none has been created
            under this Project.
          </Typography>
          <Typography>
            <strong>vi)</strong> The linkage icon changes to an Edit icon.
          </Typography>
          <Typography>
            <strong>vii)</strong> Click the red Sync button to activate.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig08_AttticConsole_ShowcasetoVariant_Synced.png"
            label="Fig 08 — Showcase successfully synced"
          />
        </Stack>

        <Milestone text="Your Showcase Experience is synced to the selected PDP." />
      </Paper>

      {/* SECTION 4: Linking 3D Configurator */}
      <Paper sx={SECTION_STYLE(COLORS.accentPurple, "#faf5ff")}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          4. Linking Products with 3D Configurator Experiences
        </Typography>

        <Typography paragraph>
          The 3D Configurator allows customers to customise products directly on
          the PDP.
        </Typography>

        <Stack spacing={2}>
          <Typography>
            <strong>i)</strong> Select the variant.
          </Typography>
          <Typography>
            <strong>ii)</strong> Click the Configurator dropdown.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig09_AttticConsole_ConfiguratortoVariant_SelectionDropdown.png"
            label="Fig 09 — Configurator dropdown"
          />

          <Typography>
            <strong>iii)</strong> Select the correct option.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig10_AttticConsole_ConfiguratortoVariant_StorefrontSelected.png"
            label="Fig 10 — Configurator selected"
          />

          <Typography>
            <strong>iv)</strong> Click Sync to publish the Configurator.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig11_AttticConsole_ConfiguratortoVariant_Synced.png"
            label="Fig 11 — Configurator successfully synced"
          />
        </Stack>

        <Milestone text="Your Configurator Experience is synced to the selected PDP." />
      </Paper>

      {/* SECTION 5: Publishing */}
      <Paper sx={SECTION_STYLE(COLORS.grayDark, "#f8fafc")}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          5. Publishing 3D Experiences on your Store
        </Typography>

        <Stack spacing={2}>
          <Typography>
            <strong>a)</strong> Go to Shopify Admin → Themes → Edit Theme.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig12_ShopifyStore_EditTheme.png"
            label="Fig 12 — Shopify theme editor"
          />

          <Typography>
            <strong>b)</strong> Select the correct PDP template.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig13_ShopifyStore_SelectPDPTemplate.png"
            label="Fig 13 — PDP template selection"
          />

          <Typography>
            <strong>c)</strong> Add the Atttic3D App block.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig14_ShopifyStore_AddAppElement.png"
            label="Fig 14 — Adding Atttic3D App"
          />

          <Typography>
            <strong>d)</strong> Buttons appear only when Experiences are
            correctly mapped.
          </Typography>

          <DocImage
            src="https://atttic-mdl.blr1.cdn.digitaloceanspaces.com/general/misc_data/console_website_assets/images/Fig15_ShopifyStore_ExperienceButtonPreview.png"
            label="Fig 15 — Experience button preview"
          />
        </Stack>

        <Milestone text="Your Atttic Experiences are now visible on PDPs in pop-up windows." />
      </Paper>
    </Container>
  );
}
